<template>
  <div class="card-wrap">
    <h3 class="title">Mapeo de columnas</h3>
    <p class="sub">
      El sistema detectó <strong>{{ fileColumns.length }}</strong> columnas en
      tu archivo.
      <span v-if="autoCount > 0">
        <strong>{{ autoCount }}</strong> se mapearon automáticamente.
      </span>
      <span v-if="missingRequired.length">
        Faltan <strong>{{ missingRequired.length }}</strong> campos requeridos.
      </span>
    </p>

    <div v-if="missingRequired.length" class="warn-banner">
      <i class="el-icon-warning-outline"></i>
      <div>
        <strong>Campos requeridos sin mapear:</strong>
        {{ missingRequiredLabels.join(", ") }}. Asígnalos en la columna derecha
        antes de continuar.
      </div>
    </div>

    <div class="legend">
      <span class="legend-label">Estado:</span>
      <span class="badge auto">
        <i class="el-icon-check"></i> Auto-detectado
      </span>
      <span class="badge manual">
        <i class="el-icon-warning-outline"></i> Manual
      </span>
      <span class="badge skip">— No importar</span>
    </div>

    <div class="table-wrap">
      <table class="map-table">
        <thead>
          <tr>
            <th>Columna en tu archivo</th>
            <th>Muestra</th>
            <th>Estado</th>
            <th>Campo destino</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(col, i) in fileColumns" :key="i">
            <td>
              <span class="col-source">{{ col }}</span>
            </td>
            <td>
              <span class="col-sample">{{ sampleFor(col) }}</span>
            </td>
            <td>
              <span :class="['badge', stateFor(col)]">
                <i :class="stateIcon(col)"></i>
                {{ stateLabel(col) }}
              </span>
            </td>
            <td>
              <el-select
                v-model="mapping[col]"
                size="small"
                placeholder="— No importar —"
                clearable
                :class="{ 'has-error': isInvalid(col) }"
              >
                <el-option label="— No importar —" value=""></el-option>
                <el-option
                  v-for="f in targetFields"
                  :key="f"
                  :label="labelFor(f)"
                  :value="f"
                >
                </el-option>
              </el-select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="module === 'invoices'" class="info-banner">
      <i class="el-icon-info"></i>
      <div>
        Para facturas: el sistema buscará al cliente por
        <strong>RUC/Cédula</strong>. Si no existe en el directorio, podrás optar
        por crearlo automáticamente en el siguiente paso.
      </div>
    </div>

    <div class="btn-row">
      <el-button plain @click="$emit('back')">← Atrás</el-button>
      <el-button
        type="primary"
        :disabled="missingRequired.length > 0"
        :loading="loading"
        @click="emitReady"
      >
        Ver vista previa →
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "StepMapping",
  props: {
    module: { type: String, required: true },
    sessionId: { type: [Number, String], default: null },
    fileColumns: { type: Array, default: () => [] },
    previewRows: { type: Array, default: () => [] },
    autoMap: { type: Object, default: () => ({}) },
    targetFields: { type: Array, default: () => [] },
    requiredFields: { type: Array, default: () => [] },
    fieldLabels: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      mapping: {},
      loading: false
    };
  },
  computed: {
    autoCount() {
      return Object.values(this.mapping).filter(
        v => v && this.autoMap[this.findColForTarget(v)] === v
      ).length;
    },
    mappedTargets() {
      return Object.values(this.mapping).filter(v => v && v !== "");
    },
    missingRequired() {
      return this.requiredFields.filter(f => !this.mappedTargets.includes(f));
    },
    missingRequiredLabels() {
      return this.missingRequired.map(f => this.labelFor(f));
    }
  },
  watch: {
    fileColumns: {
      immediate: true,
      handler() {
        this.initMapping();
      }
    },
    autoMap: {
      handler() {
        this.initMapping();
      }
    }
  },
  methods: {
    initMapping() {
      const m = {};
      (this.fileColumns || []).forEach(col => {
        m[col] = this.autoMap[col] || "";
      });
      this.mapping = m;
    },
    sampleFor(col) {
      const first = (this.previewRows && this.previewRows[0]) || {};
      const v = first[col];
      if (v === null || v === undefined) return "—";
      const s = String(v);
      return s.length > 40 ? s.slice(0, 40) + "…" : s;
    },
    findColForTarget(target) {
      return Object.keys(this.autoMap).find(c => this.autoMap[c] === target);
    },
    stateFor(col) {
      const v = this.mapping[col];
      if (!v) return "skip";
      if (this.autoMap[col] === v) return "auto";
      return "manual";
    },
    stateLabel(col) {
      const s = this.stateFor(col);
      return s === "auto" ? "Auto" : s === "manual" ? "Manual" : "Sin uso";
    },
    stateIcon(col) {
      const s = this.stateFor(col);
      if (s === "auto") return "el-icon-check";
      if (s === "manual") return "el-icon-edit-outline";
      return "el-icon-minus";
    },
    labelFor(field) {
      return this.fieldLabels[field] || field;
    },
    isInvalid(col) {
      const v = this.mapping[col];
      if (!v) return false;
      const dupes = Object.entries(this.mapping).filter(
        ([k, value]) => value === v && k !== col
      );
      return dupes.length > 0;
    },
    emitReady() {
      const cleaned = {};
      Object.keys(this.mapping).forEach(k => {
        if (this.mapping[k]) cleaned[k] = this.mapping[k];
      });
      this.loading = true;
      this.$emit("ready", cleaned);
      setTimeout(() => (this.loading = false), 600);
    }
  }
};
</script>

<style lang="scss" scoped>
.card-wrap {
  background: #fff;
  border: 0.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 1.75rem;
}

.title {
  font-size: 17px;
  font-weight: 600;
  color: #2d2b55;
  margin: 0 0 4px;
}

.sub {
  font-size: 13px;
  color: #6e7484;
  margin: 0 0 1.25rem;
}

.warn-banner {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #faeeda;
  border-left: 3px solid #ef9f27;
  border-radius: 8px;
  font-size: 12px;
  color: #633806;
  margin-bottom: 1rem;

  i {
    font-size: 16px;
    margin-top: 1px;
  }
}

.info-banner {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #e1f5ee;
  border-radius: 8px;
  font-size: 12px;
  color: #0f6e56;
  margin-top: 1rem;

  i {
    font-size: 14px;
    margin-top: 2px;
  }
}

.legend {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  .legend-label {
    font-size: 12px;
    color: #6e7484;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 20px;

  &.auto {
    background: #e1f5ee;
    color: #0f6e56;
  }

  &.manual {
    background: #faeeda;
    color: #854f0b;
  }

  &.skip {
    background: #f1efe8;
    color: #888;
  }
}

.table-wrap {
  border: 0.5px solid #e4eaf6;
  border-radius: 8px;
  overflow: hidden;
}

.map-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;

  th {
    font-size: 11px;
    font-weight: 600;
    color: #6e7484;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 12px;
    border-bottom: 0.5px solid #e4eaf6;
    text-align: left;
    background: #fafafa;
  }

  td {
    padding: 12px;
    border-bottom: 0.5px solid #e4eaf6;
    font-size: 13px;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.col-source {
  font-family: ui-monospace, "SF Mono", monospace;
  font-size: 12px;
  background: #f7f7f5;
  padding: 4px 8px;
  border-radius: 4px;
  color: #2d2b55;
}

.col-sample {
  font-size: 12px;
  color: #6e7484;
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .has-error .el-input__inner {
  border-color: #e24b4a;
}

.btn-row {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
}
</style>
